/**
 * Flushes the offline write queue when the GraphQL endpoint is reachable again.
 */
import { uploadToS3 } from "utils/aws_s3";

import { postChatAddBatch, postGql, postModuleInspectBatch } from "./gqlClient";
import { chatJobsToInputs, isChatAddJob, isEphemeralJob } from "./chatQueue";
import { coalesceInspectInputs, isModuleInspectJob } from "./inspectQueue";
import { isNetworkError, isOnline, probeOnline, subscribeNetwork } from "./network";
import { deleteSyncJob, hydrateSyncQueueFromBackup, listSyncQueue, rewriteQueuedBlobUrls } from "./queue";

let started = false;
let flushing = false;
let pendingFlush = false;
const listeners = new Set();
let lastState = { pending: 0, syncing: false };

/**
 * Notifies UI subscribers of queue size / flush progress.
 *
 * @param {Partial<{ pending: number, syncing: boolean }>} patch State patch.
 * @returns {void}
 */
const emit = (patch) => {
  lastState = { ...lastState, ...patch };
  listeners.forEach((fn) => fn(lastState));
};

/**
 * Subscribes to sync-queue status for the offline banner.
 *
 * @param {(state: { pending: number, syncing: boolean }) => void} listener Callback.
 * @returns {() => void} Unsubscribe.
 */
export const subscribeSync = (listener) => {
  listeners.add(listener);
  listener(lastState);
  return () => listeners.delete(listener);
};

/**
 * Rebuilds a File from a queued ArrayBuffer.
 *
 * @param {Object} job S3 queue record.
 * @returns {File}
 */
const fileFromJob = (job) =>
  new File([job.buffer], job.fileName || job.newFileName || "upload", {
    type: job.contentType || "application/octet-stream",
  });

const isRetryableSyncError = (err, job) => {
  const status = err?.response?.status;
  const msg = String(err?.message || "");
  return (
    isNetworkError(err) ||
    (typeof navigator !== "undefined" && navigator.onLine === false) ||
    status === 401 ||
    status === 429 ||
    status >= 500 ||
    job.kind === "s3" ||
    /internal server error|not authorised|unauthenticated|failed to fetch|network error|gql returned null|there is error in return value/i.test(msg)
  );
};

/**
 * Replays queued GraphQL mutations and S3 uploads in FIFO order.
 *
 * @returns {Promise<void>}
 */
export const flushSyncQueue = async () => {
  if (flushing) {
    pendingFlush = true;
    return;
  }
  flushing = true;
  emit({ syncing: true });
  try {
    const jobs = await listSyncQueue();
    emit({ pending: jobs.length, syncing: jobs.length > 0 });
    const sentInspectIds = new Set();
    const sentChatIds = new Set();
    for (const job of jobs) {
      try {
        if (sentInspectIds.has(job.id) || sentChatIds.has(job.id)) continue;
        if (isEphemeralJob(job)) {
          await deleteSyncJob(job.id);
          continue;
        }
        if (isModuleInspectJob(job)) {
          const inspectJobs = jobs.filter((item) => isModuleInspectJob(item) && !sentInspectIds.has(item.id));
          await postModuleInspectBatch(coalesceInspectInputs(inspectJobs.map((item) => item.variables?.input)));
          await Promise.all(
            inspectJobs.map(async (item) => {
              sentInspectIds.add(item.id);
              await deleteSyncJob(item.id);
            })
          );
          continue;
        }
        if (isChatAddJob(job)) {
          const chatJobs = jobs.filter((item) => isChatAddJob(item) && !sentChatIds.has(item.id));
          await postChatAddBatch(chatJobsToInputs(chatJobs));
          await Promise.all(
            chatJobs.map(async (item) => {
              sentChatIds.add(item.id);
              await deleteSyncJob(item.id);
            })
          );
          continue;
        }
        if (job.kind === "s3") {
          const publicUrl = await uploadToS3(fileFromJob(job), job.directory, job.newFileName, job.oldFileName, {
            skipQueue: true,
          });
          await rewriteQueuedBlobUrls(job.localUrl, publicUrl);
        } else if (job.kind === "gql") {
          await postGql(job.query, job.variables);
        }
        await deleteSyncJob(job.id);
      } catch (err) {
        if (isRetryableSyncError(err, job)) {
          emit({ pending: (await listSyncQueue()).length, syncing: false });
          return;
        }
        await deleteSyncJob(job.id);
      }
    }
    emit({ pending: (await listSyncQueue()).length, syncing: false });
  } catch {
    emit({ syncing: false });
  } finally {
    flushing = false;
    if (pendingFlush) {
      pendingFlush = false;
      flushSyncQueue();
    }
  }
};

/**
 * Probes the network, then flushes if the API is reachable.
 *
 * @returns {Promise<void>}
 */
const flushIfReachable = async () => {
  const jobs = await hydrateSyncQueueFromBackup().catch(() => listSyncQueue().catch(() => []));
  emit({ pending: Array.isArray(jobs) ? jobs.length : 0 });
  if (!jobs || !jobs.length) return;
  if (await probeOnline()) await flushSyncQueue();
};

/**
 * Starts reconnect probes: `online` event, tab focus, SW messages, and a
 * short interval while jobs are waiting (navigator.onLine can lie).
 *
 * @returns {void}
 */
export const startSyncListener = () => {
  if (started || typeof window === "undefined") return;
  started = true;

  subscribeNetwork((online) => {
    if (online) flushIfReachable();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && isOnline()) flushIfReachable();
  });

  if (navigator.serviceWorker) {
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data && event.data.type === "ASTER_SYNC") flushIfReachable();
    });
  }

  setInterval(() => {
    if (!lastState.pending) return;
    flushIfReachable();
  }, 15000);

  flushIfReachable();
};
